import { booleanToTextValue, textValueToBoolean } from "~/app/utils/helpers"
import type { CountrySales, Employment, Engagement } from "@oysterhr/schema"
import type {
  VATValues,
  AdditionalQuestionsValues,
  VATFormValues,
  VatManagementPearlFields,
} from "./types"
import { useTranslation } from "react-i18next"
import { countryCodeToName } from "@oysterhr/common"

/**
 * Returns the Pearl text (having all the translation placeholders set)
 * for the VAT management field specified in the first argument.
 */
export const useVatPearlText = (
  fieldName?: VatManagementPearlFields,
  countryCode?: Engagement["countryCode"],
) => {
  const { t } = useTranslation("knowledge_base/Default")
  if (!fieldName || !countryCode) return null

  return t(`${fieldName}`, {
    countryName: countryCodeToName(countryCode),
    countryAdjectival: countryCodeToAdjectival(countryCode),
  })
}

/**
 * To avoid confusion in the `vatManagement.workFocusedWithinCountryOfResidence` translation
 * we need to display the adjectival for each country the translation is displayed.
 * Since this is the only place where we need this, we'll keep this map limited
 * to countries included in the `workFocusedWithinCountryOfResidence`.
 */
const countryCodeToAdjectival = (countryCode: Employment["countryCode"]) => {
  const countryCodeToAdjectivalMap = {
    CN: "Chinese",
    IL: "Israeli",
    TR: "Turkish",
    CR: "Costa Rican",
    SV: "Salvadorean",
    GT: "Guatemalan",
    KE: "Kenyan",
    ZA: "South African",
    TW: "Taiwanese",
    CO: "Colombian",
    SN: "Senegalese",
    MD: "Moldovan",
    VN: "Vietnamese",
    BD: "Bangladeshi",
    NG: "Nigerian",
    BO: "Bolivian",
    SA: "Saudi Arabian",
  }
  if (!countryCode) return null
  return countryCodeToAdjectivalMap[countryCode as keyof typeof countryCodeToAdjectivalMap]
}

export const countryHasVATField = (countryCode: Employment["countryCode"]) => {
  return {
    workFocusedWithinCountryOfResidence: [
      "CN",
      "IL",
      "TR",
      "CR",
      "SV",
      "GT",
      "KE",
      "ZA",
      "TW",
      "CO",
      "SN",
      "MD",
      "VN",
      "BD",
      "NG",
      "BO",
      "SA",
    ].includes(countryCode || ""),
    sellsInCountry: ["CN", "IL", "CO", "BO"].includes(countryCode || ""),
  }
}

export const showVATQuestionsForCountry = (countryCode: string) => {
  return Object.values(countryHasVATField(countryCode)).some(Boolean)
}

export const hasAdditionalQuestions = (countryCode: string) =>
  countryHasVATField(countryCode).sellsInCountry

export const hasPreviouslySavedAnswerForAdditionalQuestions = (
  values: AdditionalQuestionsValues,
) => {
  const { sellsInCountry } = values
  return textValueToBoolean(sellsInCountry) != null
}

export const paysVATIfHasPossibleSales = (countryCode?: string) => {
  return ["CN", "IL"].includes(countryCode || "")
}

export const mapCountryToAnswerInCompany = ({
  countryCode,
  countrySales,
}: VATValues): AdditionalQuestionsValues => {
  switch (countryCode) {
    case "CN":
      return {
        sellsInCountry: booleanToTextValue(countrySales?.mightSellInChina ?? undefined),
      }
    case "IL":
      return {
        sellsInCountry: booleanToTextValue(countrySales?.mightSellInIsrael ?? undefined),
      }
    case "CO":
      return {
        sellsInCountry: booleanToTextValue(countrySales?.mightSellInColombia ?? undefined),
      }
    case "BO":
      return {
        sellsInCountry: booleanToTextValue(countrySales?.mightSellInBolivia ?? undefined),
      }
    default:
      return {}
  }
}
export const getInitialValues = ({
  countrySales,
  countryCode,
}: VATValues): AdditionalQuestionsValues => {
  return {
    ...mapCountryToAnswerInCompany({ countrySales, countryCode }),
  }
}
export const mapValuesToCompany = ({
  values,
  countryCode,
}: {
  values: VATFormValues
  countryCode: string
}): CountrySales => {
  const isWorkFocusedWithinCountryOfResidence = textValueToBoolean(
    values.workFocusedWithinCountryOfResidence,
  )
  switch (countryCode) {
    case "CN": {
      if (isWorkFocusedWithinCountryOfResidence) return {}
      return {
        mightSellInChina: textValueToBoolean(values.sellsInCountry),
      }
    }
    case "IL": {
      if (isWorkFocusedWithinCountryOfResidence) return {}
      return {
        mightSellInIsrael: textValueToBoolean(values.sellsInCountry),
      }
    }
    case "CO": {
      if (isWorkFocusedWithinCountryOfResidence) return {}
      return {
        mightSellInColombia: textValueToBoolean(values.sellsInCountry),
      }
    }
    case "BO": {
      if (isWorkFocusedWithinCountryOfResidence) return {}
      return {
        mightSellInBolivia: textValueToBoolean(values.sellsInCountry),
      }
    }
    default:
      return {}
  }
}
